import type { SavedMovie } from '../domain/model';
import { MOVIES, openDatabase, requestValue, transactionDone } from './projectRepository';

export async function putSavedMovie(movie: SavedMovie): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(MOVIES, 'readwrite');
  transaction.objectStore(MOVIES).put(movie);
  await transactionDone(transaction);
}

export async function getSavedMovie(id: string): Promise<SavedMovie | undefined> {
  const database = await openDatabase();
  const transaction = database.transaction(MOVIES, 'readonly');
  return requestValue<SavedMovie | undefined>(transaction.objectStore(MOVIES).get(id));
}

/** Lists live (non-tombstoned) movies, newest first. */
export async function listSavedMovies(): Promise<SavedMovie[]> {
  const database = await openDatabase();
  const transaction = database.transaction(MOVIES, 'readonly');
  const all = await requestValue<SavedMovie[]>(transaction.objectStore(MOVIES).index('createdAt').getAll());
  return all.filter((movie) => movie.deletedAt === undefined).reverse();
}

export async function savedMoviesTotalBytes(): Promise<number> {
  const movies = await listSavedMovies();
  return movies.reduce((sum, movie) => sum + movie.sizeBytes, 0);
}

/** Soft delete: the movie disappears from lists but can be restored during the undo window. */
export async function markSavedMovieDeleted(id: string): Promise<SavedMovie | undefined> {
  const database = await openDatabase();
  const transaction = database.transaction(MOVIES, 'readwrite');
  const store = transaction.objectStore(MOVIES);
  const movie = await requestValue<SavedMovie | undefined>(store.get(id));
  if (!movie) return undefined;
  const updated: SavedMovie = { ...movie, deletedAt: Date.now() };
  store.put(updated);
  await transactionDone(transaction);
  return updated;
}

export async function restoreSavedMovie(id: string): Promise<SavedMovie | undefined> {
  const database = await openDatabase();
  const transaction = database.transaction(MOVIES, 'readwrite');
  const store = transaction.objectStore(MOVIES);
  const movie = await requestValue<SavedMovie | undefined>(store.get(id));
  if (!movie) return undefined;
  const restored = { ...movie };
  delete restored.deletedAt;
  store.put(restored);
  await transactionDone(transaction);
  return restored;
}

/**
 * Hard-deletes tombstoned movies older than the grace window.
 * Returns the purged records so callers can remove native files.
 */
export async function purgeDeletedSavedMovies(graceMs = 0): Promise<SavedMovie[]> {
  const database = await openDatabase();
  const transaction = database.transaction(MOVIES, 'readwrite');
  const store = transaction.objectStore(MOVIES);
  const all = await requestValue<SavedMovie[]>(store.getAll());
  const cutoff = Date.now() - graceMs;
  const purged = all.filter((movie) => movie.deletedAt !== undefined && movie.deletedAt <= cutoff);
  purged.forEach((movie) => store.delete(movie.id));
  await transactionDone(transaction);
  return purged;
}
